export type TimeParts = { hours: number; minutes: number; seconds: number };

function isValidDate(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

function clampInt(n: number, min: number, max: number): number {
  const x = Math.trunc(n);
  return Math.min(max, Math.max(min, x));
}

export function extractTimeParts(raw: any): TimeParts | null {
  if (raw === null || raw === undefined) return null;

  // Angular Material timepicker sometimes returns a Date.
  if (raw instanceof Date) {
    if (!isValidDate(raw)) return null;
    return {
      hours: clampInt(raw.getHours(), 0, 23),
      minutes: clampInt(raw.getMinutes(), 0, 59),
      seconds: clampInt(raw.getSeconds(), 0, 59)
    };
  }

  // Epoch millis
  if (typeof raw === 'number') {
    const d = new Date(raw);
    if (!isValidDate(d)) return null;
    return {
      hours: clampInt(d.getHours(), 0, 23),
      minutes: clampInt(d.getMinutes(), 0, 59),
      seconds: clampInt(d.getSeconds(), 0, 59)
    };
  }

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return null;

    // Pure time string: HH:mm or HH:mm:ss
    const pureTime = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*$/.exec(s);
    if (pureTime) {
      return {
        hours: clampInt(Number(pureTime[1]), 0, 23),
        minutes: clampInt(Number(pureTime[2]), 0, 59),
        seconds: clampInt(pureTime[3] ? Number(pureTime[3]) : 0, 0, 59)
      };
    }

    // ISO-ish datetime string: capture time part after 'T'
    const isoTime = /T(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(s);
    if (isoTime) {
      return {
        hours: clampInt(Number(isoTime[1]), 0, 23),
        minutes: clampInt(Number(isoTime[2]), 0, 59),
        seconds: clampInt(isoTime[3] ? Number(isoTime[3]) : 0, 0, 59)
      };
    }

    // Fallback: let Date parse and take its clock time
    const d = new Date(s);
    if (!isValidDate(d)) return null;
    return {
      hours: clampInt(d.getHours(), 0, 23),
      minutes: clampInt(d.getMinutes(), 0, 59),
      seconds: clampInt(d.getSeconds(), 0, 59)
    };
  }

  // Some pickers return {hours, minutes} style objects.
  if (typeof raw === 'object') {
    const maybeHours = (raw as any).hours ?? (raw as any).hour;
    const maybeMinutes = (raw as any).minutes ?? (raw as any).minute;
    const maybeSeconds = (raw as any).seconds ?? (raw as any).second;

    if (maybeHours !== undefined && maybeMinutes !== undefined) {
      return {
        hours: clampInt(Number(maybeHours), 0, 23),
        minutes: clampInt(Number(maybeMinutes), 0, 59),
        seconds: clampInt(maybeSeconds !== undefined ? Number(maybeSeconds) : 0, 0, 59)
      };
    }
  }

  return null;
}

export function parseDateLike(raw: any): Date | null {
  if (raw === null || raw === undefined) return null;
  if (raw instanceof Date) {
    if (!isValidDate(raw)) return null;
    return new Date(raw.getTime());
  }
  if (typeof raw === 'number' || typeof raw === 'string') {
    const d = new Date(raw);
    if (!isValidDate(d)) return null;
    return d;
  }
  return null;
}

/**
 * Combines a date (from a date picker / datetime-local feed) with a time-only value.
 * The date part always comes from `dateRaw`; the time part always comes from `timeRaw`.
 */
export function combineDateAndTimeToIso(dateRaw: any, timeRaw: any): string | undefined {
  const date = parseDateLike(dateRaw);
  if (!date) return undefined;

  const time = extractTimeParts(timeRaw) ?? { hours: 0, minutes: 0, seconds: 0 };
  date.setHours(time.hours, time.minutes, time.seconds, 0);
  return date.toISOString();
}
